import {
  DataModelsCustomFieldKeyValue,
  GetOrganizationUnitSummaryResponse,
  SvcGetOrganizationUnitSummary,
  SvcUpdateUser,
  UsersLoggedInUserDto,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { createContext, useContext, useMemo, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import Logger from "~/services/logger/logger";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import evaUtils from "~/utils/eva-utils";

import { useConfiguration } from "./configuration";

type FavouriteStoreContext = {
  preferredStoreId?: string;
  preferredStore?: GetOrganizationUnitSummaryResponse | null;
  setPreferredStoreId: (newId: string) => void;
};

const Context = createContext<FavouriteStoreContext>({ setPreferredStoreId: (newId) => "" });

export default function FavouriteStoreProvider(props: { children: React.ReactNode }) {
  const { isAuthenticated, user, refreshUserData } = useAuthContext();
  const router = useRouter();
  const { configuration } = useConfiguration();

  const updateUser = useEvaService(SvcUpdateUser);
  const getOrganizationUnitSummary = useEvaService(SvcGetOrganizationUnitSummary);
  const evaEndpointUrl = process.env.NEXT_PUBLIC_EVA_BACKEND_URL;
  const currentCountry = crossCountryUtils.getCurrentCountryCode(router);

  const [updateLocalStorage, setUpdateLocalStorage] = useState(false);

  const getPreferredStoreFromUser = (user: UsersLoggedInUserDto) => {
    const value = evaUtils.getCustomFieldValueForResource(Constants.PREFERRED_COLLECT_ID, user, configuration);

    // Support both string and number for backwards compatibility
    // (old data might be stored as NumberValue)
    if (typeof value === "string") {
      return value;
    } else if (typeof value === "number") {
      return String(value);
    }
    return undefined;
  };

  const getPreferredStoreFromStorage = (currentCountry: string) => {
    if (typeof window !== "undefined" && localStorage?.getItem(`${currentCountry}-selectedStore`)) {
      return localStorage.getItem(`${currentCountry}-selectedStore`);
    } else {
      return;
    }
  };

  const preferredStoreId = useMemo(() => {
    // if authenticated fetch from eva user otherwise from local storage
    if (isAuthenticated && user) {
      return getPreferredStoreFromUser(user);
    }
    return getPreferredStoreFromStorage(currentCountry) ?? undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry, isAuthenticated, user, updateLocalStorage]);

  const setStoreInStorage = (storeID: string, currentCountry: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(`${currentCountry}-selectedStore`, `${storeID}`);
      setUpdateLocalStorage(!updateLocalStorage);
      // setTimeout(() => setUpdateLocalStorage(!updateLocalStorage), 1000);
    }
  };

  const setPreferredStoreId = async (newId: string) => {
    const customFields: DataModelsCustomFieldKeyValue[] = [];
    const customFieldId = evaUtils.getCustomFieldConfiguration(
      Constants.PREFERRED_COLLECT_ID,
      configuration
    )?.CustomFieldID;

    if (customFieldId) {
      // TODO: not sure if `newId` should be converted too, since it is a custom field
      customFields.push({ CustomFieldID: customFieldId, NumberValue: Number(newId) });
    }

    if (isAuthenticated && user && customFields.length > 0) {
      try {
        await updateUser({ ID: user.ID, CustomFields: customFields });
        await refreshUserData();
      } catch (error) {
        Logger.instance.error("FavouriteStore: Failed to update preferred store", error);
        throw error;
      }
    } else {
      setStoreInStorage(newId, currentCountry);
    }
  };

  const preferredStoreQuery = useQuery({
    queryKey: ["preferredStore", preferredStoreId],
    staleTime: Infinity,
    keepPreviousData: true,
    queryFn: async () => {
      const data = await getOrganizationUnitSummary({
        OrganizationUnitID: preferredStoreId!,
      });

      if (!data) {
        return null;
      }

      return data;
    },
    enabled: !!evaEndpointUrl && !!preferredStoreId,
  });

  return (
    <Context.Provider
      value={{
        preferredStoreId: preferredStoreId,
        preferredStore: preferredStoreQuery.data,
        setPreferredStoreId,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

export function useFavouriteStore() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useFavouriteStore must be used within a FavouriteStoreProvider`);
  }

  return context;
}
